/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

import { store } from '@/state/state';

import { ckeditorApi } from '../../../../services/api/ckeditorService';
import {
  Suggestion,
  UpdateSuggestion,
} from '../../../../services/types/ckeditor';

const {
  CKEditor: { Plugin },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

interface TrackChangesIntegrationConfig {
  documentId: string;
}

export class TrackChangesIntegration extends Plugin {
  static get requires() {
    return ['TrackChanges', 'UsersIntegration'];
  }

  static get pluginName() {
    return 'TrackChangesIntegration';
  }

  init() {
    const trackChangesPlugin = this.editor.plugins.get('TrackChanges');
    const usersPlugin = this.editor.plugins.get('Users');
    const config = this.editor.config.get(
      'trackChangesIntegration',
    ) as TrackChangesIntegrationConfig;
    const defaultUserId = 'anonymous-user';

    // Set the adapter to the `TrackChanges#adapter` property.
    trackChangesPlugin.adapter = {
      getSuggestion: async (suggestionId: string) => {
        // Use the API directly (not through hooks as we're in a class)
        const response = await store.dispatch(
          ckeditorApi.endpoints.getSuggestion.initiate({
            documentId: config.documentId,
            suggestionId,
          }),
        );

        if ('error' in response || !response.data) {
          throw new Error('Error fetching suggestion');
        }

        // Transform API response to expected format
        return {
          id: response.data.suggestion_id,
          type: response.data.type,
          authorId: response.data.author_id,
          createdAt: response.data.created_at
            ? new Date(response.data.created_at)
            : new Date(),
          data: response.data.data ? JSON.parse(response.data.data) : null,
          attributes: response.data.attributes || {},
          hasComments: response.data.has_comments,
        };
      },

      addSuggestion: async (suggestionData) => {
        const currentUserId = usersPlugin.me?.id || defaultUserId;

        // Transform to API expected format
        const apiSuggestion: Suggestion = {
          suggestion_id: suggestionData.id,
          original_suggestion_id:
            suggestionData.originalSuggestionId || suggestionData.id,
          type: suggestionData.type,
          author_id: currentUserId,
          has_comments: suggestionData.hasComments || false,
          data: suggestionData.data
            ? JSON.stringify(suggestionData.data)
            : undefined,
          attributes: suggestionData.attributes,
        };

        const response = await store.dispatch(
          ckeditorApi.endpoints.createSuggestion.initiate({
            documentId: config.documentId,
            suggestion: apiSuggestion,
          }),
        );

        if ('error' in response || !response.data) {
          throw new Error('Error creating suggestion');
        }

        const createdSuggestion = response.data as Suggestion;
        return {
          id: createdSuggestion.suggestion_id,
          type: createdSuggestion.type,
          authorId: createdSuggestion.author_id,
          createdAt: createdSuggestion.created_at
            ? new Date(createdSuggestion.created_at)
            : new Date(),
          data: createdSuggestion.data
            ? JSON.parse(createdSuggestion.data)
            : null,
          attributes: createdSuggestion.attributes || {},
        };
      },

      updateSuggestion: async (id: string, suggestionData) => {
        // Transform to API expected format
        const apiSuggestion: UpdateSuggestion = {
          suggestion_id: id,
          has_comments: suggestionData.hasComments,
          attributes: suggestionData.attributes,
        };

        await store.dispatch(
          ckeditorApi.endpoints.updateSuggestion.initiate({
            documentId: config.documentId,
            suggestionId: id,
            suggestion: apiSuggestion,
          }),
        );
      },
    };
  }
}
